import {
  Box,
  Button,
  Center,
  Checkbox,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import CommonTopArt from 'src/components/icons/CommonTopArt'
import { PagePadding } from 'src/components/layout/PagePadding'
import { PageRoot } from 'src/components/layout/PageRoot'
import { AccountLockedBanner } from 'src/components/login/AccountLockedBanner'
import { useConnectParams } from 'src/connect/useParams'
import { pagesPath } from 'src/routes/$path'
import { fontSizes } from 'src/theme/theme'
import {
  EXTERNAL_TRANSMISSION,
  PHARMACY_TERMS,
  PRIVACY_POLICY,
  SECURITY_WHITEPAPER,
} from 'src/urls'
import { LoginTitle } from 'src/utils/loginTitle'
import {
  compose,
  validateEmailPattern,
  validateMaxLength,
  validateRequired,
} from 'src/utils/validations'

type FormValues = {
  email: string
  password: string
}

export const IndexCommon: FC<{
  loginTitle: LoginTitle
  onSubmitEmailLogin: (values: FormValues) => void
  submitting: boolean
  locked?: boolean
}> = ({ loginTitle, onSubmitEmailLogin, submitting, locked = false }) => {
  const { getUrlQuery } = useConnectParams()
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
  })

  const email = watch('email')
  const password = watch('password')
  const canSubmit = Boolean(email && password)

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)

  return (
    <PageRoot bg="cds.neutralGray.100">
      <Container maxWidth="100%">
        <Center>
          <Box
            bg="white"
            px="80px"
            py="64px"
            mx="auto"
            mt={12}
            height="auto"
            gap="24px"
            width="560px"
            sx={{ boxShadow: '0px 8px 20px 0px #00000014' }}
          >
            <Box>
              <Box mb={6}>
                <Center>
                  <Stack spacing={6} align="center">
                    <CommonTopArt />
                    <Text
                      fontSize={fontSizes['2xl']}
                      fontWeight="600"
                      color={loginTitle.isEnvBadge ? 'cds.red.600' : undefined}
                    >
                      {loginTitle.text}
                    </Text>
                  </Stack>
                </Center>
              </Box>
              <Stack spacing={4} align="start">
                <Box>
                  <Text>ご登録のメールアドレスとパスワードでログインしてください。</Text>
                </Box>
              </Stack>
              {locked && (
                <Box mt="16px" mb="24px">
                  <AccountLockedBanner />
                </Box>
              )}
            </Box>
            <form onSubmit={handleSubmit(v => onSubmitEmailLogin(v))}>
              <Box mt="32px">
                <FormControl id="email" isInvalid={!!errors.email}>
                  <FormLabel fontWeight="600" color="gray.800">
                    メールアドレス
                  </FormLabel>
                  <Input
                    type="text"
                    size="lg"
                    inputMode="email"
                    placeholder="メールアドレスを入力"
                    {...register(
                      'email',
                      compose(
                        validateRequired,
                        validateMaxLength(76),
                        validateEmailPattern,
                      )('メールアドレス'),
                    )}
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>
                <FormControl id="password" mt={6} isInvalid={!!errors.password}>
                  <FormLabel fontWeight="600" color="gray.800">
                    パスワード
                  </FormLabel>
                  <Input
                    type={passwordVisible ? 'text' : 'password'}
                    size="lg"
                    placeholder="パスワードを入力"
                    {...register('password', validateRequired('パスワード'))}
                  />
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>
                <Box mt={4}>
                  <Checkbox
                    checked={passwordVisible}
                    onChange={e => setPasswordVisible(e.target.checked)}
                  >
                    パスワードを表示する
                  </Checkbox>
                </Box>
              </Box>
              <Box mt={8}>
                <Text>
                  <Link
                    href={PHARMACY_TERMS}
                    isExternal
                    color="green.600"
                    textDecoration="underline"
                    mx={1}
                  >
                    利用規約
                  </Link>
                  および
                  <Link
                    href={PRIVACY_POLICY}
                    isExternal
                    color="green.600"
                    textDecoration="underline"
                    mx={1}
                  >
                    プライバシーポリシー
                  </Link>
                  ・
                  <Link
                    href={EXTERNAL_TRANSMISSION}
                    isExternal
                    color="green.600"
                    textDecoration="underline"
                    mx={1}
                  >
                    外部送信ポリシー
                  </Link>
                  に同意される方のみ、ログインください。
                </Text>
              </Box>
              <Box mt={6}>
                <Button
                  width="100%"
                  size="lg"
                  fontWeight="bold"
                  type="submit"
                  bg="cds.green.700"
                  color="white"
                  _hover={{ bg: 'cds.green.700' }}
                  isDisabled={locked || !canSubmit}
                  isLoading={submitting}
                >
                  ログイン
                </Button>
              </Box>
              <Box mt={6} textAlign="center">
                <NextLink href={pagesPath.recovery.email.$url(getUrlQuery())} legacyBehavior>
                  <Link color="green.600" textDecoration="underline">
                    パスワードがわからない方はこちら
                  </Link>
                </NextLink>
              </Box>
            </form>
          </Box>
        </Center>
      </Container>
      <Container mt={12}>
        <Center>
          <Link href={SECURITY_WHITEPAPER} color="green.600" textDecoration="underline" isExternal>
            セキュリティホワイトペーパー
          </Link>
        </Center>
      </Container>
      <PagePadding />
      <Box px={6} py={4} textAlign="left">
        <Text fontSize={fontSizes.md} color="cds.neutralGray.600">
          © MICIN, Inc.
        </Text>
      </Box>
    </PageRoot>
  )
}
